import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { LandingRedirect } from '@/pages/LandingRedirect';
import { LoginPage } from '@/pages/LoginPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

import { RequireAuth } from './RequireAuth';

const AuthenticatedAppShell = lazy(() =>
  import('@/components/layout/AuthenticatedAppShell').then((module) => ({
    default: module.AuthenticatedAppShell,
  }))
);
const ProjectDetailPage = lazy(() =>
  import('@/pages/ProjectDetailPage').then((module) => ({ default: module.ProjectDetailPage }))
);
const CreateProjectPage = lazy(() =>
  import('@/pages/CreateProjectPage').then((module) => ({ default: module.CreateProjectPage }))
);
const ProjectsPage = lazy(() =>
  import('@/pages/ProjectsPage').then((module) => ({ default: module.ProjectsPage }))
);
const SettingsHubPage = lazy(() =>
  import('@/pages/settings/SettingsHubPage').then((module) => ({
    default: module.SettingsHubPage,
  }))
);
const UntriagedTasksPage = lazy(() =>
  import('@/pages/UntriagedTasksPage').then((module) => ({ default: module.UntriagedTasksPage }))
);
const VesselOverviewPage = lazy(() =>
  import('@/pages/VesselOverviewPage').then((module) => ({
    default: module.VesselOverviewPage,
  }))
);
const DocumentRegisterPage = lazy(() =>
  import('@/pages/project/DocumentRegisterPage').then((module) => ({
    default: module.DocumentRegisterPage,
  }))
);
const GADrawingsPage = lazy(() =>
  import('@/pages/project/GADrawingsPage').then((module) => ({ default: module.GADrawingsPage }))
);
const GALayoutViewerPage = lazy(() =>
  import('@/pages/project/GALayoutViewerPage').then((module) => ({
    default: module.GALayoutViewerPage,
  }))
);
const GanttPage = lazy(() =>
  import('@/pages/project/GanttPage').then((module) => ({ default: module.GanttPage }))
);
const HandoverPage = lazy(() =>
  import('@/pages/project/HandoverPage').then((module) => ({ default: module.HandoverPage }))
);
const MeetingMinutesPage = lazy(() =>
  import('@/pages/project/MeetingMinutesPage').then((module) => ({
    default: module.MeetingMinutesPage,
  }))
);
const OpenQuestionsPage = lazy(() =>
  import('@/pages/project/OpenQuestionsPage').then((module) => ({
    default: module.OpenQuestionsPage,
  }))
);
const OverviewPage = lazy(() =>
  import('@/pages/project/OverviewPage').then((module) => ({ default: module.OverviewPage }))
);
const ProcurementPage = lazy(() =>
  import('@/pages/project/ProcurementPage').then((module) => ({ default: module.ProcurementPage }))
);
const PunchListPage = lazy(() =>
  import('@/pages/project/PunchListPage').then((module) => ({ default: module.PunchListPage }))
);
const ReportsPage = lazy(() =>
  import('@/pages/project/ReportsPage').then((module) => ({ default: module.ReportsPage }))
);
const ScopeChangesPage = lazy(() =>
  import('@/pages/project/ScopeChangesPage').then((module) => ({
    default: module.ScopeChangesPage,
  }))
);
const ScopeOfWorkPage = lazy(() =>
  import('@/pages/project/ScopeOfWorkPage').then((module) => ({ default: module.ScopeOfWorkPage }))
);
const SiteDiaryPage = lazy(() =>
  import('@/pages/project/SiteDiaryPage').then((module) => ({ default: module.SiteDiaryPage }))
);
const StagePaymentsPage = lazy(() =>
  import('@/pages/project/StagePaymentsPage').then((module) => ({
    default: module.StagePaymentsPage,
  }))
);
const TeamPage = lazy(() =>
  import('@/pages/project/TeamPage').then((module) => ({ default: module.TeamPage }))
);
const WorkBreakdownPage = lazy(() =>
  import('@/pages/project/WorkBreakdownPage').then((module) => ({
    default: module.WorkBreakdownPage,
  }))
);
const WorkItemDetailPage = lazy(() =>
  import('@/pages/project/WorkItemDetailPage').then((module) => ({
    default: module.WorkItemDetailPage,
  }))
);

function DeferredPage({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-40 items-center justify-center text-sm text-muted-foreground">
          Loading...
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

function deferred(children: ReactNode) {
  return <DeferredPage>{children}</DeferredPage>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<RequireAuth>{deferred(<AuthenticatedAppShell />)}</RequireAuth>}>
        {topLevelRoutes}
        {workItemDetailRoutes}
        {projectRoutes}
      </Route>
      <Route path="/" element={<Navigate to="/vessel/overview" replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

const topLevelRoutes = (
  <>
    <Route path="/dashboard" element={<LandingRedirect />} />
    <Route path="/vessel" element={<Navigate to="/vessel/overview" replace />} />
    <Route path="/vessel/overview" element={deferred(<VesselOverviewPage />)} />
    <Route path="/projects" element={deferred(<ProjectsPage />)} />
    <Route path="/action-required" element={deferred(<UntriagedTasksPage />)} />
    <Route path="/untriaged-tasks" element={<Navigate to="/action-required" replace />} />
    <Route path="/reports" element={<Navigate to="/vessel/overview" replace />} />
    <Route path="/settings" element={deferred(<SettingsHubPage />)} />
    <Route path="/settings/profile" element={<Navigate to="/settings?tab=account" replace />} />
    <Route path="/settings/vessel" element={<Navigate to="/settings?tab=vessel" replace />} />
    <Route
      path="/settings/notifications"
      element={<Navigate to="/settings?tab=notifications" replace />}
    />
    <Route path="/settings/help" element={<Navigate to="/settings?tab=help" replace />} />
    <Route path="/settings/templates" element={<Navigate to="/settings?tab=templates" replace />} />
    <Route path="/admin" element={<Navigate to="/settings" replace />} />
    <Route path="/admin/templates" element={<Navigate to="/settings?tab=templates" replace />} />
    <Route
      path="/scope-of-work-templates"
      element={<Navigate to="/settings?tab=templates" replace />}
    />
    <Route path="/projects/new" element={deferred(<CreateProjectPage />)} />
  </>
);

const workItemDetailRoutes = (
  <>
    <Route
      path="/projects/:projectId/work-breakdown/:workItemId"
      element={deferred(<ProjectDetailPage />)}
    >
      <Route index element={deferred(<WorkItemDetailPage />)} />
    </Route>
    <Route path="/projects/:projectId/ga/:layoutId" element={deferred(<ProjectDetailPage />)}>
      <Route index element={deferred(<GALayoutViewerPage />)} />
    </Route>
  </>
);

const projectRoutes = (
  <Route path="/projects/:projectId" element={deferred(<ProjectDetailPage />)}>
    <Route index element={<Navigate to="overview" replace />} />
    <Route path="overview" element={deferred(<OverviewPage />)} />
    <Route path="team" element={deferred(<TeamPage />)} />
    <Route path="work" element={<Navigate to="../work-breakdown" replace />} />
    <Route path="work-breakdown" element={deferred(<WorkBreakdownPage />)} />
    <Route path="work-breakdown/:workItemId" element={deferred(<WorkItemDetailPage />)} />
    <Route path="schedule" element={<Navigate to="../gantt" replace />} />
    <Route path="gantt" element={deferred(<GanttPage />)} />
    <Route path="procurement" element={deferred(<ProcurementPage />)} />
    <Route path="scope-changes" element={deferred(<ScopeChangesPage />)} />
    <Route path="log" element={<Navigate to="../questions" replace />} />
    <Route path="comms" element={<Navigate to="../questions" replace />} />
    <Route path="questions" element={deferred(<OpenQuestionsPage />)} />
    <Route path="diary" element={deferred(<SiteDiaryPage />)} />
    <Route path="minutes" element={deferred(<MeetingMinutesPage />)} />
    <Route path="registers" element={<Navigate to="../scope-of-work" replace />} />
    <Route path="scope-of-work" element={deferred(<ScopeOfWorkPage />)} />
    <Route path="punch-list" element={deferred(<PunchListPage />)} />
    <Route path="documents" element={deferred(<DocumentRegisterPage />)} />
    <Route path="ga" element={deferred(<GADrawingsPage />)} />
    <Route path="ga/:layoutId" element={deferred(<GALayoutViewerPage />)} />
    <Route path="stage-payments" element={deferred(<StagePaymentsPage />)} />
    <Route path="reports" element={deferred(<ReportsPage />)} />
    <Route path="handover" element={deferred(<HandoverPage />)} />
  </Route>
);
